/**
 * 套餐、价格与计费规则 —— 契约 §3.4 / §3.5 的唯一真源。
 *
 * v1.1.0 起档位从 `free | basic | pro | unlimited` 重划为
 * `free | lite | plus | pro | max`（+ 内部 `unlimited`），并新增价格与计费纯函数。
 * 变更登记在 `docs/CHANGELOG-contract.md`，破坏性同步清单见
 * `docs/superpowers/specs/2026-10-05-payment-checkout-design.md` §4.4。
 *
 * 两条贯穿全仓的原则：
 * 1. 数据库/Redis 里只存"用户当前计划与已用量"，**限额与价格永远由这里推导**，
 *    避免出现"改了代码但历史数据里还是旧价格/旧限额"的双真源问题；
 * 2. 因此**服务端算钱只认这张表，绝不接受客户端传金额**。
 *
 * 只放常量与纯函数，不引 zod：额度计算会在 Redis 热路径上被反复调用，
 * 保持这个模块无依赖可以让它被任意端安全引用（小程序也直接吃源码）。
 */

export const PLAN_IDS = ['free', 'lite', 'plus', 'pro', 'max', 'unlimited'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/**
 * 对外售卖的档位。
 *
 * `free` 不是商品（注册即赠），`unlimited` 是内部档位（见下），
 * 两者都**不接受下单** —— 支付请求体的 schema 用这个列表而不是 `PLAN_IDS`，
 * 让"买 free"这种请求在入口就被 40001 拦掉，而不是走到业务层再判。
 */
export const PAID_PLAN_IDS = ['lite', 'plus', 'pro', 'max'] as const;
export type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

/**
 * 「无限额度」套餐的 id（见 `docs/CHANGELOG-contract.md` C11）。
 *
 * 它**不是**一个对外售卖的档位，而是两件事的载体：
 * 1. 演示账号/试听课账号/内部员工号 —— 这些账号存在的意义就是"能一直用"，
 *    一旦被额度拦住（`42900`），演示者在台上看到的是一个答不了话的产品；
 * 2. 独立的 planId 值 —— 前端据此把额度条渲染成「不限」而不是一个天文数字。
 *
 * 授予方式见 `apps/server/src/quota/plan.config.ts` 的 `planIdOf()`：
 * 只有服务端配置里显式列出的 userId 才会拿到它，**默认没有任何账号是 unlimited**。
 */
export const UNLIMITED_PLAN_ID = 'unlimited';

/**
 * 「不限」的限额哨兵值。
 *
 * ★ v1.3.0 起这是**纯服务端常量**：绝对 token 量（限额与已用量）一律不下发到客户端，
 * 对外只有 `QuotaSnapshot` 的百分比（见 `quotaSnapshotOf()`）。前端因此不再需要、
 * 也不允许 import `PLAN_LIMITS` / `UNLIMITED_TOKEN_LIMIT`（有 `scripts/verify-contract.mjs`
 * 的客户端剥离检查兜底）。
 *
 * 服务端判断"无限档"**必须**用 `isUnlimitedQuotaLimit(limit)`，不许自己写
 * `limit === Number.MAX_SAFE_INTEGER`：后者一旦哨兵换值就会静默失效。
 */
export const UNLIMITED_TOKEN_LIMIT = Number.MAX_SAFE_INTEGER;

export function isUnlimitedQuotaLimit(limit: number): boolean {
  return limit === UNLIMITED_TOKEN_LIMIT;
}

export interface PlanLimits {
  readonly planId: PlanId;
  readonly dailyTokenLimit: number;
  readonly monthlyTokenLimit: number;
}

/**
 * 冻结数值，不得在运行时被修改。
 *
 * `free` 的**日额度是哨兵**（用户决策 m00053「free 额度改为100000 tokens/月，
 * 不设置日额度上限」）：`cache.reserve()` 的比较是 `current + amount > limit`，
 * 哨兵之下永远为假，于是日闸形同不存在、**只剩月额度一道闸**；
 * 展示层靠 `isUnlimitedQuotaLimit()` 把它渲染成「不限」。
 *
 * 付费档位的月额度 = 日额度 × 30（用户决策）。
 */
export const PLAN_LIMITS: Readonly<Record<PlanId, PlanLimits>> = Object.freeze({
  free: Object.freeze({
    planId: 'free',
    dailyTokenLimit: UNLIMITED_TOKEN_LIMIT,
    monthlyTokenLimit: 100_000,
  }),
  lite: Object.freeze({ planId: 'lite', dailyTokenLimit: 30_000, monthlyTokenLimit: 900_000 }),
  plus: Object.freeze({ planId: 'plus', dailyTokenLimit: 90_000, monthlyTokenLimit: 2_700_000 }),
  pro: Object.freeze({ planId: 'pro', dailyTokenLimit: 300_000, monthlyTokenLimit: 9_000_000 }),
  max: Object.freeze({ planId: 'max', dailyTokenLimit: 600_000, monthlyTokenLimit: 18_000_000 }),
  unlimited: Object.freeze({
    planId: UNLIMITED_PLAN_ID,
    dailyTokenLimit: UNLIMITED_TOKEN_LIMIT,
    monthlyTokenLimit: UNLIMITED_TOKEN_LIMIT,
  }),
});

/**
 * 档位高低次序。**只在这一个地方定义**，升级/降级的判定全部走它。
 *
 * 用显式数字而不是 `PLAN_IDS.indexOf()`：数组顺序是"展示顺序"，
 * 未来插一个中间档位不应该悄悄改变已有档位的相对高低 —— 这两件事必须解耦。
 */
export const PLAN_RANKS: Readonly<Record<PlanId, number>> = Object.freeze({
  free: 0,
  lite: 1,
  plus: 2,
  pro: 3,
  max: 4,
  unlimited: 5,
});

/** 展示名（用户决策 m00091：直接用英文，不翻译） */
export const PLAN_LABELS: Readonly<Record<PlanId, string>> = Object.freeze({
  free: 'Free',
  lite: 'Lite',
  plus: 'Plus',
  pro: 'Pro',
  max: 'Max',
  unlimited: 'Unlimited',
});

export const BILLING_CYCLES = ['monthly', 'yearly'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

/**
 * 价格表，单位**分**（仓库无 Decimal 先例，金额一律整数分）。
 *
 * 年付 = 10 个月价（用户决策）：lite ¥15/月 → ¥150/年，依此类推。
 * 折算成元/千 token 后单调递减：0.500 / 0.444 / 0.400 / 0.333，
 * 也就是"买得越多单价越低"这条定价意图在数据上真的成立。
 */
export const PLAN_PRICES: Readonly<Record<PaidPlanId, Readonly<Record<BillingCycle, number>>>> =
  Object.freeze({
    lite: Object.freeze({ monthly: 1_500, yearly: 15_000 }),
    plus: Object.freeze({ monthly: 4_000, yearly: 40_000 }),
    pro: Object.freeze({ monthly: 12_000, yearly: 120_000 }),
    max: Object.freeze({ monthly: 20_000, yearly: 200_000 }),
  });

/** 订单有效期（用户决策 m00091：「15分钟」） */
export const ORDER_TTL_SECONDS = 900;

export const PAYMENT_CHANNELS = ['wechat', 'alipay'] as const;
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number];

export const ORDER_STATUSES = ['pending', 'paid', 'closed', 'failed'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_KINDS = ['purchase', 'renewal', 'upgrade'] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];

/** 新注册用户默认套餐（契约 §3.4："默认注册赠送"） */
export const DEFAULT_PLAN_ID: PlanId = 'free';

/**
 * 请求前预扣的 token 上限（Lead Directives §5.5）。
 * 2000 是「最大输入 + 典型输出」的经验上界：预扣过小会让并发请求挤爆日额度，
 * 过大则会让用户在只有几轮对话时就被误判额度不足。
 */
export const QUOTA_RESERVE_TOKENS = 2000;

/** 日额度重置时区基准：契约 resetAt 为 ISO-8601 UTC 字符串，按 UTC 自然日重置 */
export const QUOTA_RESET_TIMEZONE = 'UTC';

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === 'string' && (PLAN_IDS as readonly string[]).includes(value);
}

export function isPaidPlanId(value: unknown): value is PaidPlanId {
  return typeof value === 'string' && (PAID_PLAN_IDS as readonly string[]).includes(value);
}

export function isBillingCycle(value: unknown): value is BillingCycle {
  return typeof value === 'string' && (BILLING_CYCLES as readonly string[]).includes(value);
}

export function planLimitsOf(planId: PlanId): PlanLimits {
  return PLAN_LIMITS[planId];
}

export function planRankOf(planId: PlanId): number {
  return PLAN_RANKS[planId];
}

export function planLabelOf(planId: PlanId): string {
  return PLAN_LABELS[planId];
}

/** 单价（分）。free / unlimited 不是商品，恒为 0，调用方不需要先判 `isPaidPlanId` */
export function planPriceOf(planId: PlanId, cycle: BillingCycle): number {
  return isPaidPlanId(planId) ? PLAN_PRICES[planId][cycle] : 0;
}

/** 分 → 展示用元字符串（去掉多余的 `.00`）：1500 → `"15"`、1550 → `"15.50"` */
export function formatPriceYuan(fen: number): string {
  return (fen / 100).toFixed(2).replace(/\.00$/, '');
}

/** 下一次日额度重置时间（UTC 次日 00:00:00.000Z） */
export function nextDailyResetAt(now: Date): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0));
  return next.toISOString();
}

/** 下一次月额度重置时间（UTC 次月 1 日 00:00:00.000Z） */
export function nextMonthlyResetAt(now: Date): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0));
  return next.toISOString();
}

/**
 * 当前月额度窗口的起点（UTC 当月 1 日 00:00:00.000Z）。
 *
 * 与 `nextMonthlyResetAt()` 配对，构成半开区间 [startOfUtcMonth, nextMonthlyResetAt)——
 * 契约 §3.4 的按会话用量默认就用这个窗口，好让页面上的"合计"和额度条的月度用量
 * 说的是同一段时间。
 */
export function startOfUtcMonth(now: Date): string {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
  return start.toISOString();
}

/* ------------------------------------------------------------------ *
 * 额度快照：服务端内部真值 → 对外的百分比（v1.3.0）
 * ------------------------------------------------------------------ */

export const QUOTA_SCOPES = ['daily', 'monthly'] as const;
export type QuotaScope = (typeof QUOTA_SCOPES)[number];

/**
 * 服务端内部的额度真值。
 *
 * ★ 这个结构**只允许活在服务端进程里**：它装着限额与已用量（绝对 token 数），
 * 任何响应体都不得包含它。对外的形状是下面的 `QuotaSnapshot`（只有百分比）。
 * 之所以仍要留一个"有数字"的结构：预扣拦截、对账（验收 §4.8）、以及
 * `GET /quota/ledger*` 的聚合都需要真值，把这些数字从服务端也删掉等于放弃对账能力。
 */
export interface QuotaState {
  planId: PlanId;
  dailyTokenLimit: number;
  dailyTokenUsed: number;
  monthlyTokenLimit: number;
  monthlyTokenUsed: number;
}

/** 用契约里的套餐表把"已用量"补成完整内部真值（限额永远由 `PLAN_LIMITS` 推导）。 */
export function buildQuotaState(planId: PlanId, dailyTokenUsed: number, monthlyTokenUsed: number): QuotaState {
  const limits = PLAN_LIMITS[planId];
  return {
    planId,
    dailyTokenLimit: limits.dailyTokenLimit,
    dailyTokenUsed,
    monthlyTokenLimit: limits.monthlyTokenLimit,
    monthlyTokenUsed,
  };
}

/**
 * 对外的额度快照（契约 §2 `Quota`，v1.3.0 起）：只有百分比，没有任何 token 量。
 *
 * `resetAt` 说的是 `scope` 那一维的重置时刻——它和 `scope` 必须成对理解，
 * 否则界面会出现"本月剩余 3%"配"今日 24:00 重置"这种自相矛盾的文案。
 */
export interface QuotaSnapshot {
  planId: PlanId;
  scope: QuotaScope;
  /** 0-100 的整数百分比，**由服务端算好**；客户端不许再用限额自行换算。 */
  remainingPercent: number;
  /** 该账号两个维度都不设上限（内部 `unlimited` 档）。 */
  unlimited: boolean;
  resetAt: string;
}

/**
 * 内部真值 → 对外百分比。规则（用户裁决 2026-10-05）：
 *
 * - 只给**一个**百分比，取"当前生效维度"= 两个维度里剩余比例更小的那个，也就是
 *   用户**最先撞上**的那一维。免费档的日维度不设上限，于是它自然落在月度上；
 *   付费档平时落在日度，月末月度更紧时自动切到月度——这比"按档位写死维度"更准。
 * - `remainingPercent` **向下取整**并夹在 `[0,100]`：宁可少报剩余，也不让进度条虚报。
 * - `unlimited` 仅在两个维度都不设上限时为 true。
 */
export function quotaSnapshotOf(state: QuotaState, now: Date = new Date()): QuotaSnapshot {
  const dailyRatio = ratioOrNull(state.dailyTokenLimit, state.dailyTokenUsed);
  const monthlyRatio = ratioOrNull(state.monthlyTokenLimit, state.monthlyTokenUsed);

  if (dailyRatio === null && monthlyRatio === null) {
    return {
      planId: state.planId,
      scope: 'monthly',
      remainingPercent: 100,
      unlimited: true,
      resetAt: nextMonthlyResetAt(now),
    };
  }

  // 比例更小者胜；两者相等（如都刚重置）时取日维度——它的重置更近，反馈更快。
  const useDaily = dailyRatio !== null && (monthlyRatio === null || dailyRatio <= monthlyRatio);
  const scope: QuotaScope = useDaily ? 'daily' : 'monthly';

  return {
    planId: state.planId,
    scope,
    remainingPercent: quotaRemainingPercentOf(state, scope),
    unlimited: false,
    resetAt: quotaResetAtOf(scope, now),
  };
}

/**
 * 指定维度的剩余百分比（0-100 整数，向下取整，超发夹到 0）。
 *
 * 单独拆出来是给 **42900 的 details** 用的：那里要用"被拦下的那一维"，
 * 而 `quotaSnapshotOf()` 报的是"最先撞上的那一维"——预扣失败的那一维才是用户
 * 此刻需要看的东西，两者不是同一个维度时不能混用。上限缺失/为哨兵时按 100 处理
 * （该维不设闸，剩余量在展示上没有意义）。
 */
export function quotaRemainingPercentOf(state: QuotaState, scope: QuotaScope): number {
  const limit = scope === 'daily' ? state.dailyTokenLimit : state.monthlyTokenLimit;
  const used = scope === 'daily' ? state.dailyTokenUsed : state.monthlyTokenUsed;
  const ratio = ratioOrNull(limit, used);
  return ratio === null ? 100 : Math.min(100, Math.max(0, Math.floor(ratio * 100)));
}

/** 指定维度的重置时刻。与 `scope` 必须成对下发，否则文案会自相矛盾。 */
export function quotaResetAtOf(scope: QuotaScope, now: Date): string {
  return scope === 'daily' ? nextDailyResetAt(now) : nextMonthlyResetAt(now);
}

/** 剩余比例；上限缺失/为 0（配置异常，除零无意义）或哨兵时返回 null。 */
function ratioOrNull(limit: number, used: number): number | null {
  if (limit <= 0 || isUnlimitedQuotaLimit(limit)) {
    return null;
  }
  return Math.max(0, Math.min(1, (limit - used) / limit));
}

/* ------------------------------------------------------------------ *
 * 套餐卡片的额度文案（只给相对倍率，不给绝对 token 量）
 * ------------------------------------------------------------------ */

/** 相对 Lite 的额度倍率。倍数不是 token 量，可以安全地出现在客户端。 */
export const QUOTA_TIER_MULTIPLIERS: Readonly<Record<PaidPlanId, number>> = Object.freeze({
  lite: tierMultiplierOf('lite'),
  plus: tierMultiplierOf('plus'),
  pro: tierMultiplierOf('pro'),
  max: tierMultiplierOf('max'),
});

function tierMultiplierOf(planId: PaidPlanId): number {
  return Math.round(PLAN_LIMITS[planId].monthlyTokenLimit / PLAN_LIMITS.lite.monthlyTokenLimit);
}

/**
 * 套餐卡片上的一句话额度说明。
 *
 * v1.3.0 之前这里是「每日 30,000 tokens · 每月 900,000 tokens」，现在改用倍率：
 * 卡片仍然要能说清"贵在哪"，但绝对 token 量不再离开服务端。
 */
export function quotaTierLabelOf(planId: PaidPlanId): string {
  const multiplier = QUOTA_TIER_MULTIPLIERS[planId];
  return multiplier <= 1 ? '基础额度' : `额度约为 Lite 的 ${multiplier} 倍`;
}

/* ------------------------------------------------------------------ *
 * 计费：到期时间与差价
 * ------------------------------------------------------------------ */

/**
 * 到期时刻的时区基准（用户决策：次月同日 **23:59:59.999 Asia/Shanghai**）。
 *
 * 写死 8 小时偏移而不是引 IANA 时区库：中国全境单一时区且不实行夏令时，
 * 这个偏移在可预见的将来是常量；而引入 `Intl`/时区库会让这个"零依赖模块"
 * 第一次拥有运行期依赖，代价远大于收益。
 */
const CHINA_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * 从 `anchor` 起算一个计费周期，返回对齐到自然月/年末尾的瞬间（ISO-8601 UTC）。
 *
 * 例：`2026-10-05T12:00:00.000Z`（上海 10-05 20:00）月付 →
 * `2026-11-05T15:59:59.999Z`（上海 11-05 23:59:59.999）。
 *
 * 月末溢出**回退**到目标月最后一天（1/31 月付 → 2/28；闰年 2/29 年付 → 次年 2/28）：
 * 向上溢出会让用户凭空多得几天，且"3/2 买的 2/31"这种日期根本不存在。
 */
export function addBillingCycle(anchorIso: string, cycle: BillingCycle): string {
  const wall = new Date(Date.parse(anchorIso) + CHINA_OFFSET_MS);
  const year = wall.getUTCFullYear();
  const month = wall.getUTCMonth();
  const day = wall.getUTCDate();

  const rawMonth = cycle === 'monthly' ? month + 1 : month;
  const rawYear = cycle === 'monthly' ? year : year + 1;
  const targetYear = rawYear + Math.floor(rawMonth / 12);
  const targetMonth = ((rawMonth % 12) + 12) % 12;

  // `Date.UTC(y, m + 1, 0)` = 目标月最后一天，自动处理闰年
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const targetDay = Math.min(day, lastDay);

  const endMs =
    Date.UTC(targetYear, targetMonth, targetDay, 23, 59, 59, 999) - CHINA_OFFSET_MS;
  return new Date(endMs).toISOString();
}

/** 当前生效订阅的最小描述（服务端从 Subscription 表投影，前端从 `GET /subscription` 拿） */
export interface SubscriptionWindow {
  readonly planId: PlanId;
  readonly billingCycle: BillingCycle;
  readonly startsAt: string;
  readonly expiresAt: string;
}

export interface PlanChangeQuote {
  readonly kind: OrderKind;
  readonly planId: PaidPlanId;
  readonly billingCycle: BillingCycle;
  /** 应付金额（分）。服务端落库用的就是这个数，客户端传来的金额一律忽略 */
  readonly amountFen: number;
  readonly startsAt: string;
  readonly expiresAt: string;
}

export type PlanChangeRefusalReason = 'downgrade-not-allowed' | 'cycle-mismatch';

export type PlanChangeResult =
  | { readonly ok: true; readonly quote: PlanChangeQuote }
  | {
      readonly ok: false;
      readonly reason: PlanChangeRefusalReason;
      readonly currentPlanId: PlanId;
      readonly currentBillingCycle: BillingCycle;
    };

/**
 * 升级差价（用户决策 m00091：「差价额度根据当前套餐剩余天数正比计算」）。
 *
 * 把"当前档位在剩余期间内的价值"从"目标档位同期间的价值"里扣掉：
 *
 * ```
 * ratio    = (expiresAt - now) / (expiresAt - startsAt)      // 剩余比例，clamp 到 (0,1]
 * amountFen = max(1, round(目标档价 × ratio) - round(当前档价 × ratio))
 * ```
 *
 * 两道计算各自先取整再相减（而不是先相减再取整），保证**两边用的是同一个
 * 分位舍入规则**，升级差价不会因为浮点尾差变成 ±1 分。
 * 下限 1 分：0 元订单在支付渠道侧会被拒，也让"是否真的产生了交易"变得不可判。
 */
export function upgradeAmountFen(
  current: SubscriptionWindow,
  target: PaidPlanId,
  now: Date = new Date(),
): number {
  const startMs = Date.parse(current.startsAt);
  const endMs = Date.parse(current.expiresAt);
  const span = endMs - startMs;
  if (!Number.isFinite(span) || span <= 0) return planPriceOf(target, current.billingCycle);

  const remaining = Math.min(Math.max(endMs - now.getTime(), 0), span);
  const ratio = remaining / span;

  const targetShare = Math.round(planPriceOf(target, current.billingCycle) * ratio);
  const currentShare = Math.round(planPriceOf(current.planId, current.billingCycle) * ratio);
  return Math.max(1, targetShare - currentShare);
}

/**
 * 一次下单尝试的完整报价 —— **服务端与前端共用这一个函数**。
 *
 * 共用的意义：用户在页面上看到的"应付 ¥X、有效期至 Y"与服务端最终落库的
 * 金额和区间来自同一段代码，不存在"前端预览与后端算账各写一遍然后对不上"。
 * 前端还可以用 `ok === false` 直接把按钮置灰（例如已是 pro 时不允许再选 lite）。
 *
 * 服务端把两种拒绝理由**统一**翻译成 `40900 CONFLICT`（契约 §3.5 规则表），
 * 具体是哪一种放在 `details.reason` 里（等于 `PlanChangeRefusalReason` 的取值）：
 * - `downgrade-not-allowed` → `40900` + `details.reason = 'downgrade-not-allowed'`
 * - `cycle-mismatch`        → `40900` + `details.reason = 'cycle-mismatch'`
 *
 * ★ 为什么 `cycle-mismatch` 也是 409 而不是 40001：`40001 INVALID_PARAM` 的语义是
 * "请求本身不合法"，而这里 `planId` 与 `billingCycle` 都是合法枚举值、单看请求挑不出毛病，
 * 拒绝的理由完全来自**服务端当前的订阅状态**（在途订阅是月付，跨到年付无法按剩余天数折算）。
 * 同一个"拒绝变更"语义若分裂成两个错误码，前端的升级引导文案就会分叉成两套，
 * 而两者要传达的动作其实是同一件事：**不能这样升，改成同周期**。
 */
export function quotePlanChange(
  current: SubscriptionWindow | null,
  target: PaidPlanId,
  cycle: BillingCycle,
  now: Date = new Date(),
): PlanChangeResult {
  const nowIso = now.toISOString();
  const active = current !== null && Date.parse(current.expiresAt) > now.getTime() ? current : null;

  // 无有效订阅（从未购买 / 已到期）→ 新购，与"到期后自由选择档位"一致
  if (active === null) {
    return {
      ok: true,
      quote: {
        kind: 'purchase',
        planId: target,
        billingCycle: cycle,
        amountFen: planPriceOf(target, cycle),
        startsAt: nowIso,
        expiresAt: addBillingCycle(nowIso, cycle),
      },
    };
  }

  const currentRank = planRankOf(active.planId);
  const targetRank = planRankOf(target);

  if (targetRank < currentRank) {
    return {
      ok: false,
      reason: 'downgrade-not-allowed',
      currentPlanId: active.planId,
      currentBillingCycle: active.billingCycle,
    };
  }

  if (targetRank === currentRank) {
    // 续费顺延：从"当前到期时刻"续，而不是从现在续 —— 未到期就续费不该吃掉剩余时长
    const anchorIso =
      Date.parse(active.expiresAt) > now.getTime() ? active.expiresAt : nowIso;
    return {
      ok: true,
      quote: {
        kind: 'renewal',
        planId: target,
        billingCycle: cycle,
        amountFen: planPriceOf(target, cycle),
        startsAt: anchorIso,
        expiresAt: addBillingCycle(anchorIso, cycle),
      },
    };
  }

  // 升级：只有同周期才存在唯一的"当前档位对应价"可供按比例抵扣
  if (cycle !== active.billingCycle) {
    return {
      ok: false,
      reason: 'cycle-mismatch',
      currentPlanId: active.planId,
      currentBillingCycle: active.billingCycle,
    };
  }

  return {
    ok: true,
    quote: {
      kind: 'upgrade',
      planId: target,
      billingCycle: cycle,
      amountFen: upgradeAmountFen(active, target, now),
      // 升级不改变周期边界：startsAt 保持不变，否则连续升级的剩余比例会失真
      startsAt: active.startsAt,
      expiresAt: active.expiresAt,
    },
  };
}

/**
 * 当前生效档位：白名单 > 未过期订阅 > free。
 *
 * 这个函数是三端（服务端额度判定、前端展示、测试）共用的兜底规则，
 * 放在 contracts 里而不是服务端，是为了让"订阅过期后回到 free"这条语义
 * 不需要连数据库就能被断言。
 */
export function effectivePlanId(
  subscription: SubscriptionWindow | null,
  now: Date = new Date(),
): PlanId {
  if (subscription === null) return DEFAULT_PLAN_ID;
  return Date.parse(subscription.expiresAt) > now.getTime()
    ? subscription.planId
    : DEFAULT_PLAN_ID;
}
